import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { HandCoins, Lock, Trash2 } from "@fleet/icons";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  TextField,
} from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { billingQueryKeys, type components, formatCents } from "@aula/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type AdminRevenueShare = components["schemas"]["AdminRevenueShareResponse"];

/**
 * Admin panel for AUTHOR REVENUE SHARE (phase 2 of monetization, without gateway).
 *
 * The admin defines a period (window + pool in chosen currency); the
 * SERVER measures engagement (lessons completed by students in courses of
 * each author, self-study excluded) and splits the pool pro-rata without losing a
 * cent (largest remainder). draft = recomputable (delete and recreate);
 * settle = consolidated and immutable (accounting snapshot).
 */
export function RevenueShareManager() {
  const { t, locale } = useI18n();
  const { can } = useAdminCapabilities();
  const canWrite = () => can("billing.write");

  const query = createApiQuery<AdminRevenueShare, ErrorModel>(
    () => billingQueryKeys.adminRevenueShare(),
    async () => api.GET("/api/admin/revenue-share/periods", {}),
    () => ({ staleTime: 5000 }),
  );

  const createMutation = createApiMutation(
    async (vars: { periodStart: string; periodEnd: string; poolCents: number; currency: string }) =>
      api.POST("/api/admin/revenue-share/periods", { body: vars }),
  );
  const settleMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/revenue-share/periods/{id}/settle", {
      params: { path: { id: vars.id } },
    }),
  );
  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.DELETE("/api/admin/revenue-share/periods/{id}", { params: { path: { id: vars.id } } }),
  );

  const periods = createMemo(() => query.data?.periods ?? []);
  const refetch = () => {
    void query.refetch();
  };

  // ── Alta de periodo ────────────────────────────────────────────────────────
  const [start, setStart] = createSignal("");
  const [end, setEnd] = createSignal("");
  const [pool, setPool] = createSignal("");
  const [currency, setCurrency] = createSignal("USD");

  const poolCents = createMemo(() => {
    const value = Number.parseFloat(pool());
    return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : null;
  });
  const canCreate = () =>
    canWrite() && start() !== "" && end() !== "" && end() > start() && poolCents() !== null;

  const createPeriod = () => {
    const cents = poolCents();
    if (cents === null) {
      return;
    }
    createMutation.mutate(
      {
        // oxlint-disable-next-line lingui/no-unlocalized-strings -- sufijo ISO de medianoche UTC, no UI copy
        periodStart: new Date(`${start()}T00:00:00Z`).toISOString(),
        // oxlint-disable-next-line lingui/no-unlocalized-strings -- sufijo ISO de medianoche UTC, no UI copy
        periodEnd: new Date(`${end()}T00:00:00Z`).toISOString(),
        poolCents: cents,
        currency: currency().trim().toUpperCase() || "USD",
      },
      {
        onSuccess: () => {
          refetch();
          setPool("");
          showToast(t(msg`Revenue share period created`), "success");
        },
        onError: () => {
          showToast(t(msg`Could not create the period (window overlap or invalid data)`), "error");
        },
      },
    );
  };

  const settlePeriod = (id: string) => {
    settleMutation.mutate(
      { id },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Period settled. It is now immutable.`), "success");
        },
        onError: () => {
          showToast(t(msg`Could not settle the period`), "error");
        },
      },
    );
  };

  const deletePeriod = (id: string) => {
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Draft period deleted`), "success");
        },
      },
    );
  };

  const dateRange = (startIso: string, endIso: string): string => {
    const fmt = new Intl.DateTimeFormat(locale(), { dateStyle: "medium" });
    return `${fmt.format(new Date(startIso))} – ${fmt.format(new Date(endIso))}`;
  };

  return (
    <div class="space-y-6" data-testid="revenue-share-manager">
      <div class="flex items-center gap-3">
        <HandCoins class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-2xl font-bold tracking-tight">{t(msg`Author revenue share`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Define a period and its revenue pool; the server measures learner engagement per author and splits the pool pro-rata. Settled periods are immutable.`,
            )}
          </p>
        </div>
      </div>

      {/* Alta de periodo */}
      <Card data-testid="revenue-share-create">
        <CardHeader>
          <CardTitle>{t(msg`New period`)}</CardTitle>
        </CardHeader>
        <CardContent class="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
          <div class="space-y-1">
            <Label for="rs-start">{t(msg`From`)}</Label>
            <TextField
              id="rs-start"
              type="date"
              value={start()}
              disabled={!canWrite()}
              data-testid="revenue-share-start"
              onInput={(e) => {
                setStart(e.currentTarget.value);
              }}
            />
          </div>
          <div class="space-y-1">
            <Label for="rs-end">{t(msg`To (exclusive)`)}</Label>
            <TextField
              id="rs-end"
              type="date"
              value={end()}
              disabled={!canWrite()}
              data-testid="revenue-share-end"
              onInput={(e) => {
                setEnd(e.currentTarget.value);
              }}
            />
          </div>
          <div class="space-y-1">
            <Label for="rs-pool">{t(msg`Revenue pool`)}</Label>
            <TextField
              id="rs-pool"
              type="number"
              min="0"
              step="0.01"
              placeholder="100.00"
              value={pool()}
              disabled={!canWrite()}
              data-testid="revenue-share-pool"
              onInput={(e) => {
                setPool(e.currentTarget.value);
              }}
            />
          </div>
          <div class="space-y-1">
            <Label for="rs-currency">{t(msg`Currency`)}</Label>
            <TextField
              id="rs-currency"
              value={currency()}
              disabled={!canWrite()}
              data-testid="revenue-share-currency"
              onInput={(e) => {
                setCurrency(e.currentTarget.value);
              }}
            />
          </div>
          <Button
            disabled={!canCreate() || createMutation.isPending}
            onClick={createPeriod}
            data-testid="revenue-share-create-button"
          >
            {t(msg`Compute split`)}
          </Button>
        </CardContent>
      </Card>

      {/* Periodos */}
      {/* P23/I3 · «no hay respuesta» ≠ «no hay nada»: en error `isLoading`
          vale false, así que el guard de abajo dejaba pasar y la pantalla
          pintaba el estado VACÍO. Hay que ENVOLVER el bloque, no encadenar
          otra condición al mismo `<Show>` (pintaría error Y esqueleto). */}
      <Show when={query.isError}>
        <QueryErrorState
          testId="admin-revenue-share-error"
          compact
          onRetry={() => void query.refetch()}
        />
      </Show>
      <Show when={!query.isError}>
        <Show when={!query.isLoading} fallback={<Skeleton class="h-40 w-full rounded-xl" />}>
          <Show
            when={periods().length > 0}
            fallback={
              <p class="text-sm text-muted-foreground" data-testid="revenue-share-empty">
                {t(msg`No revenue share periods yet.`)}
              </p>
            }
          >
            <For each={periods()}>
              {(detail) => (
                <Card data-testid={`revenue-share-period-${detail.period.id}`}>
                  <CardHeader>
                    <CardTitle class="flex items-center justify-between gap-3">
                      <span class="flex items-center gap-2 min-w-0">
                        <span class="truncate">
                          {dateRange(detail.period.periodStart, detail.period.periodEnd)}
                        </span>
                        <Badge
                          variant={detail.period.status === "settled" ? "default" : "secondary"}
                          data-testid={`revenue-share-status-${detail.period.id}`}
                        >
                          {detail.period.status === "settled" ? t(msg`Settled`) : t(msg`Draft`)}
                        </Badge>
                      </span>
                      <span class="font-bold shrink-0">
                        {formatCents(detail.period.poolCents, detail.period.currency, locale())}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent class="space-y-3">
                    <Show
                      when={detail.payouts.length > 0}
                      fallback={
                        <p class="text-sm text-muted-foreground">
                          {t(msg`No learner engagement in this window — nothing to split.`)}
                        </p>
                      }
                    >
                      <div class="space-y-1">
                        <For each={detail.payouts}>
                          {(payout) => (
                            <div class="flex items-center justify-between gap-3 text-sm">
                              <span class="font-mono text-xs truncate">{payout.authorId}</span>
                              <span class="text-muted-foreground text-xs shrink-0">
                                {t(msg`Lessons`)}: {payout.engagementUnits}
                              </span>
                              <span class="font-semibold shrink-0">
                                {formatCents(payout.shareCents, detail.period.currency, locale())}
                              </span>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>
                    <Show when={detail.period.status === "draft"}>
                      <div class="flex gap-2">
                        <Button
                          size="sm"
                          disabled={!canWrite() || settleMutation.isPending}
                          onClick={() => {
                            settlePeriod(detail.period.id);
                          }}
                          data-testid={`revenue-share-settle-${detail.period.id}`}
                        >
                          <Lock class="h-4 w-4 mr-1" />
                          {t(msg`Settle`)}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!canWrite() || deleteMutation.isPending}
                          onClick={() => {
                            deletePeriod(detail.period.id);
                          }}
                          data-testid={`revenue-share-delete-${detail.period.id}`}
                        >
                          <Trash2 class="h-4 w-4 mr-1" />
                          {t(msg`Delete draft`)}
                        </Button>
                      </div>
                    </Show>
                  </CardContent>
                </Card>
              )}
            </For>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
